import type { MyMembershipResponse } from '@league/shared/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';

import type { AllowanceResponse, MarketListResponse, PlansResponse, VenueListResponse } from '@league/shared/contract';
import { formatMoney } from '@league/shared/money';
import { atLocalHourOn, atLocalTimeOn, US_TIME_ZONES, weekdayIn } from '@league/shared/time';

import { Button, ErrorNote, Field, Input } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import { zoneName } from '../../lib/when';
import { LEAGUE_SESSIONS } from '@league/shared/hq';
import { allowedCardYears, seasonFor } from '@league/shared/season';

import { NO_VENUE, VenueChooser, venueChoiceComplete, venuePayload } from './VenueChooser';

/**
 * Creating a league — the first thing a director does, and the thing M1 is measured on: 28 players
 * and its sessions, start to finish, in under twenty minutes.
 *
 * Everything here has a default that is right most of the time. A director running their autumn
 * league should be able to type a name, pick a date, and press the button.
 */

/**
 * The smallest league that can seat a table. Mahjong is played four to a table, so a league below
 * this cannot run a single one.
 *
 * One constant rather than a number typed into the input, the check and the message separately —
 * the owner has said this may rise, and three copies is how two of them get missed.
 */
const MIN_PLAYERS = 4;

export function NewLeaguePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [marketId, setMarketId] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newState, setNewState] = useState('');
  const [newZone, setNewZone] = useState('');
  const [venue, setVenue] = useState(NO_VENUE);
  const [firstSessionAt, setFirstSessionAt] = useState('');
  /**
   * WHEN EACH SESSION STARTS AND STOPS — hers to choose (Courtney, 2026-10-12). Every session was
   * saved at 7pm until then. Read in the city's time zone, never the computer's.
   */
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const timesBackwards = startTime !== '' && endTime !== '' && endTime <= startTime;
  const [playerCap, setPlayerCap] = useState('28');
  /**
   * HOW MANY SESSIONS — any number from one to twenty-four, typed by her (Courtney, 2026-10-19).
   * It replaced three named lengths (Short 3, Standard 6, Full 8). Empty until she types one: there
   * is no default length. The schedule below follows it; until it is a usable number it shows none.
   */
  const [sessionCountText, setSessionCountText] = useState('');
  const sessionCount = Number(sessionCountText);
  const countUsable =
    sessionCountText.trim() !== '' && Number.isInteger(sessionCount) && sessionCount >= LEAGUE_SESSIONS.min && sessionCount <= LEAGUE_SESSIONS.max;
  const weeks = countUsable ? sessionCount : 0;

  /**
   * WHICH NMJL CARD THIS LEAGUE IS PLAYED ON.
   *
   * Defaulted from the first session's date and then editable, which is the honest arrangement: the
   * card changes every spring, so the date gets it right almost always — and a league beginning in
   * the week either side of the changeover is a judgement she makes, not one a date can settle.
   *
   * It matters because season rankings are built from raw point totals, and a total scored on one
   * card is not comparable with one scored on another.
   */
  const [cardYear, setCardYear] = useState<number | null>(null);

  /*
    The card the first session falls on, unless she has said otherwise. `seasonFor` carries the
    rule — a session in January to March belongs to the PREVIOUS year's card — so this is right
    across new year without a special case here.
  */
  const suggestedCardYear = useMemo(() => {
    if (!firstSessionAt) return null;
    const [year, month] = firstSessionAt.split('-').map(Number);
    if (!year || !month) return null;
    return seasonFor(year, month).cardYear;
  }, [firstSessionAt]);

  const effectiveCardYear = cardYear ?? suggestedCardYear ?? new Date().getUTCFullYear();

  /*
    WHEN THE CARD IS HERS TO CHANGE, and when it is simply a fact.

    Owner's rule: the NMJL releases its card after Lunar New Year, and it reaches most people in
    mid-March and some not until mid-April. So March and April are the only two months where a
    league could honestly be played on either card — her players may or may not have the new one in
    their hands. In January and February the new card does not exist yet, and from May everybody has
    it. Those months have nothing to decide.

    Outside that window the field is shown rather than offered. It is derived correctly from the
    date, and an editable box invites the one mistake that cannot be seen afterwards: a February
    league "corrected" from 2026 to 2027 files a whole season's scores against a card nobody
    played on, and every screen still looks right.
  */
  const firstSessionMonth = firstSessionAt ? Number(firstSessionAt.split('-')[1]) : null;
  const inCardChangeover = firstSessionMonth === 3 || firstSessionMonth === 4;

  /**
   * January and February show LAST year's card, which reads as a mistake until it is explained.
   * March is in that state too, but it is also in the changeover window and gets that hint instead.
   */
  const cardLooksSurprising = firstSessionMonth !== null && firstSessionMonth <= 3;

  /** The same rule the server enforces, so the form can never offer a card the route refuses. */
  const cardChoices = useMemo(() => {
    if (!firstSessionAt) return [];
    const [year, month] = firstSessionAt.split('-').map(Number);
    if (!year || !month) return [];
    return allowedCardYears(year, month);
  }, [firstSessionAt]);

  const markets = useQuery({
    queryKey: ['markets'],
    queryFn: () => apiFetch<MarketListResponse>('/api/markets'),
  });

  const venues = useQuery({
    queryKey: ['venues', marketId],
    queryFn: () => apiFetch<VenueListResponse>(`/api/markets/${marketId}/venues`),
    enabled: marketId !== '' && marketId !== 'new',
  });

  /**
   * THE ZONE THE TYPED DATE MEANS. Either the chosen market's, or the one she is picking for the
   * market she is adding. Until there is one, there is no evening to store: "7pm on the 14th" is
   * not a moment until somebody says where. So the form waits rather than guessing.
   */
  const zone = useMemo(() => {
    if (marketId === 'new') return newZone;
    return markets.data?.markets.find((m) => m.id === marketId)?.timeZone ?? '';
  }, [marketId, newZone, markets.data]);

  /**
   * The weekday is DERIVED from the date rather than asked for separately. A league runs on the
   * session of its first session by definition, and two fields that must agree is two fields that
   * can disagree.
   */
  const weekday = useMemo(() => {
    if (!firstSessionAt || !zone) return null;
    const parsed = atLocalHourOn(firstSessionAt, zone, 19);
    /* Read back in the LEAGUE's zone — `getDay()` would answer for the browser's. */
    return Number.isNaN(parsed.getTime()) ? null : weekdayIn(parsed, zone);
  }, [firstSessionAt, zone]);

  /**
   * SESSIONS SHE HAS MOVED HERSELF, by position.
   *
   * The schedule is weekly by default and stays derived — so changing the first date shifts every
   * session that is still on its default, and leaves alone the one she deliberately moved to avoid
   * a holiday. Only a change of LENGTH clears these, because a six-session schedule cannot keep the
   * eighth date of an eight-session one.
   */
  const [movedDates, setMovedDates] = useState<Record<number, string>>({});

  /** The weekly default for each position, before any of her changes. */
  /**
   * The weekly default for each position, as a plain `yyyy-mm-dd` calendar date.
   *
   * KEPT AS A DATE STRING RATHER THAN AN INSTANT, because that is what a date input reads and
   * writes, and because turning it into an instant and back is where a timezone gets to move a
   * session by a day. The instant is built once, at submission, in the league's own zone.
   */
  const defaultDates = useMemo(() => {
    if (!firstSessionAt) return [];

    /*
      A week at a time from the DATE, not by adding 168 hours. Eight sessions can cross a
      daylight-saving change, and 7pm plus 168 hours is 6pm or 8pm on the far side of one.
    */
    const [y, m, d] = firstSessionAt.split('-').map(Number);
    return Array.from({ length: weeks }, (_, offset) => {
      const day = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + offset * 7));
      const mm = String(day.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(day.getUTCDate()).padStart(2, '0');
      return `${day.getUTCFullYear()}-${mm}-${dd}`;
    });
  }, [firstSessionAt, weeks]);

  /** What the form shows: her date where she moved one, the weekly default everywhere else. */
  const scheduleFields = useMemo(
    () => defaultDates.map((fallback, index) => movedDates[index] ?? fallback),
    [defaultDates, movedDates],
  );

  /** The same list as instants, at her start and stop times in the league's own zone. Only built to submit. */
  const sessionDates = useMemo(
    () => (zone && startTime ? scheduleFields.map((value) => atLocalTimeOn(value, zone, startTime)) : []),
    [scheduleFields, zone, startTime],
  );
  const sessionEnds = useMemo(
    () => (zone && endTime ? scheduleFields.map((value) => atLocalTimeOn(value, zone, endTime)) : []),
    [scheduleFields, zone, endTime],
  );

  const market = markets.data?.markets.find((m) => m.id === marketId);
  const venueList = venues.data?.venues ?? [];

  /*
    WHETHER THIS LEAGUE COSTS EXTRA, answered by the server rather than worked out here.

    The count of her unfinished leagues and what a membership includes are both facts the API
    already holds, and a second copy of the sum in the browser is a second place for it to be wrong
    — the screen would eventually disagree with the invoice.
  */
  const allowanceQuery = useQuery({
    queryKey: ['billing', 'allowance'],
    queryFn: () => apiFetch<AllowanceResponse>('/api/billing/allowance'),
  });

  const catalogue = useQuery({
    queryKey: ['plans'],
    queryFn: () => apiFetch<PlansResponse>('/api/public/plans'),
  });

  const allowance = allowanceQuery.data?.allowance ?? null;
  const extraPrice = catalogue.data?.plans.find((plan) => plan.key === 'extra_league_monthly');

  /*
    Unlimited (`included === null`) is never chargeable, and neither is a director still under her
    allowance. Nothing is shown while the answer is unknown — a charge notice that flickers in after
    the form has rendered is worse than one that arrives a beat late.
  */
  const membership = useQuery({ queryKey: ['billing', 'me'], queryFn: () => apiFetch<MyMembershipResponse>('/api/billing/me') });
  const renewalBlocked = (membership.data?.renewal?.mayWrite === false || membership.data?.cancellation?.mayWrite === false);
  const capacityFull = allowance?.billingMode === 'purchased' && allowance.active >= (allowance.purchasedAllowance ?? 0);
  const willBeCharged =
    allowance?.billingMode !== 'purchased' &&
    allowance !== null &&
    allowance.included !== null &&
    allowance.active >= allowance.included;

  const create = useMutation({
    mutationFn: async () => {
      return apiPost<{ league: { id: string } }>('/api/leagues', {
        name,
        ...(marketId === 'new'
          ? { newMarket: { city: newCity, state: newState.toUpperCase(), timeZone: newZone } }
          : { marketId }),
        ...venuePayload(venue, venueList),
        weekday,
        firstSessionAt: atLocalTimeOn(firstSessionAt, zone, startTime).toISOString(),
        sessionDates: sessionDates.map((date) => date.toISOString()),
        sessionEnds: sessionEnds.map((date) => date.toISOString()),
        sessionCount,
        cardYear: effectiveCardYear,
        playerCap: Number(playerCap),
      });
    },
    onSuccess: async ({ league }) => {
      await queryClient.invalidateQueries();
      navigate(`/director/leagues/${league.id}`);
    },
  });

  const error =
    create.error instanceof ApiError ? create.error.message : create.error ? 'Something went wrong.' : null;

  /*
    Below the minimum is a warning rather than a silent refusal: the button being disabled with no
    explanation is the version of this that makes somebody think the form is broken.

    An empty box is not an error — she is mid-edit, and shouting at somebody who has deleted a
    number to type a new one is worse than saying nothing.
  */
  const capBelowMinimum = playerCap.trim() !== '' && Number(playerCap) < MIN_PLAYERS;

  /*
    An EMPTY box blocks the button without showing the warning. It would post 0 and be refused by
    the server, so letting it through only moves the failure somewhere less helpful — but somebody
    who has just cleared the field to retype it has not made a mistake yet.
  */
  const capUsable = playerCap.trim() !== '' && Number(playerCap) >= MIN_PLAYERS;

  const canSubmit =
    capUsable &&
    countUsable &&
    name.trim().length > 0 &&
    firstSessionAt !== '' &&
    startTime !== '' &&
    endTime !== '' &&
    !timesBackwards &&
    venueChoiceComplete(venue, venueList) &&
    /* And a zone — the market's or the one she picked. Without it there is no evening to store. */
    zone !== '' &&
    (marketId === 'new' ? newCity.trim() !== '' && newState.trim().length === 2 : marketId !== '');

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-8 px-6 py-10">
      <header className="flex flex-col gap-1">
        <Link to="/director/leagues" className={`self-start text-[13px] ${LINK_CLASS}`}>
          ← All leagues
        </Link>
        <h1 className="font-display text-3xl leading-tight">New league</h1>
        <p className="text-[14px] text-ink-2">
          Set your league schedule now. You can adjust individual session dates later if needed.
        </p>
      </header>

      {/*
        ⚠ WHAT THIS ONE COSTS, AT THE TOP, BEFORE SHE HAS FILLED ANYTHING IN.

        The charge is automatic — the platform counts her unfinished leagues and Stripe bills the
        ones past her allowance — so there is nothing to agree to and no confirmation step. That
        makes the telling the whole of the protection: without it, the first she hears of the charge
        is an invoice three weeks later, which is how a fair charge becomes a complaint.

        ⚠ IT SAT ABOVE THE BUTTON AND THE STICKY ACTION BAR CLIPPED IT. Rendered, the second line
        read "...stops when you" and then disappeared under the bar — the half of the message that
        does the reassuring. Moving it here fixes that and is better anyway: she learns what it costs
        before she invests ten minutes in the form, not after.

        Shown ONLY when this league will actually be chargeable. A director creating her second has
        nothing to be warned about, and a notice that appears every time stops being read by the time
        it matters.

        Every figure comes from the plans table. Not one is typed here.
      */}
      {capacityFull ? <p role="alert" className="text-[14px] text-signal-d">Your selected allowance is full. Complete or archive a league to free space, or contact us to discuss a capacity increase. No additional charge has been made.</p> : null}
      {willBeCharged && extraPrice ? (
        <div className="rounded-md border border-signal-d/30 bg-tint-signal px-5 py-4">
          <p className="text-[14px] font-medium text-signal-d">
            This will be league {(allowance?.active ?? 0) + 1}, and it adds{' '}
            {formatMoney(extraPrice.amountCents, extraPrice.currency)} a month.
          </p>
          <p className="mt-1.5 max-w-[38rem] text-[13px] leading-relaxed text-ink-2">
            Your membership includes {allowance?.included}. The charge starts when you create this
            league and stops when you finish or archive it &mdash; it follows what you are running,
            so it never adds up over time.
          </p>
        </div>
      ) : null}

      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="League name" htmlFor="name" hint="Players see this. “Charlotte Autumn 2026”.">
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
        </Field>

        <Field label="Market" htmlFor="market">
          <select
            id="market"
            value={marketId}
            onChange={(e) => {
              setMarketId(e.target.value);
              /* A new city has no venues to pick from, so a venue there can only be a new one. */
              setVenue(e.target.value === 'new' ? { ...NO_VENUE, venueId: 'new' } : NO_VENUE);
            }}
            className="h-11 w-full rounded-md border border-control-border bg-paper px-3 text-[15px] focus-visible:border-deep"
            required
          >
            <option value="">Choose…</option>
            {markets.data?.markets.map((market) => (
              <option key={market.id} value={market.id}>
                {market.city}, {market.state}
              </option>
            ))}
            <option value="new">+ Somewhere new</option>
          </select>
        </Field>

        {marketId === 'new' ? (
          <div className="flex flex-col gap-3 rounded-md bg-sand p-4">
            <div className="grid grid-cols-[1fr_5rem] gap-3">
              <Field label="City" htmlFor="city">
                <Input id="city" value={newCity} onChange={(e) => setNewCity(e.target.value)} />
              </Field>
              <Field label="State" htmlFor="state">
                <Input
                  id="state"
                  value={newState}
                  onChange={(e) => setNewState(e.target.value.toUpperCase())}
                  maxLength={2}
                  placeholder="NC"
                />
              </Field>
            </div>
            {/*
              ASKED, NEVER GUESSED. Thirteen states run two zones — Nashville is Central and
              Knoxville is Eastern — so there is no state to look this up from, and no default that
              is not wrong for somebody. It has no pre-selected option for the same reason.
            */}
            <Field
              label="Time zone"
              htmlFor="zone"
              hint="Every date and start time for leagues here is read in this zone."
            >
              <select
                id="zone"
                value={newZone}
                onChange={(e) => setNewZone(e.target.value)}
                className="h-11 w-full rounded-md border border-control-border bg-paper px-3 text-[15px] focus-visible:border-deep"
                required
              >
                <option value="">Choose…</option>
                {US_TIME_ZONES.map((z) => (
                  <option key={z.zone} value={z.zone}>
                    {z.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        ) : null}

        {marketId ? (
          <VenueChooser
            idPrefix="new-league"
            venues={venueList}
            pick={marketId !== 'new'}
            choice={venue}
            defaultCity={marketId === 'new' ? newCity : (market?.city ?? '')}
            defaultState={marketId === 'new' ? newState : (market?.state ?? '')}
            venueHint="Optional — you can add it once it's booked."
            nameHint="Optional. Where the league actually plays."
            onChange={setVenue}
          />
        ) : null}

        <Field
          label="Number of sessions"
          htmlFor="session-count"
          {...(sessionCountText.trim() !== '' && !countUsable
            ? { error: `Enter a number from ${LEAGUE_SESSIONS.min} to ${LEAGUE_SESSIONS.max}.` }
            : {})}
        >
          <Input
            id="session-count"
            type="number"
            inputMode="numeric"
            min={LEAGUE_SESSIONS.min}
            max={LEAGUE_SESSIONS.max}
            required
            value={sessionCountText}
            onChange={(e) => setSessionCountText(e.target.value)}
            className="tabular w-28"
          />
        </Field>

        {/*
          THE CARD, and why a director is asked at all.

          Hand values change with each year's card, so a score is only comparable with another
          played on the same one. The first session's date answers it correctly almost every time —
          this field exists for the league that starts the week the card changes, which is hers to
          decide rather than a date's.
        */}
        {firstSessionAt ? (
          <Field
            label="Which NMJL card?"
            htmlFor="cardYear"
            hint={
              inCardChangeover
                ? 'New cards arrive between mid-March and mid-April. Pick whichever your players will actually have.'
                : cardLooksSurprising
                  ? 'The new card is not out yet — it arrives after Lunar New Year, from mid-March.'
                  : 'Everyone is on the current card from May.'
            }
          >
            {inCardChangeover ? (
              /*
                TWO BUTTONS, NEVER A NUMBER FIELD. Only two cards exist as far as a league is
                concerned, and a box that accepts 2019 or 2031 invites a typo whose damage is
                invisible afterwards. `allowedCardYears` is the same function the server checks
                against, so the form cannot offer something the route would refuse.
              */
              <div className="flex gap-2" id="cardYear">
                {cardChoices.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => setCardYear(year)}
                    aria-pressed={effectiveCardYear === year}
                    className={[
                      'tabular min-h-[44px] rounded-md border px-5 text-[14px]',
                      effectiveCardYear === year
                        ? 'border-deep bg-deep text-on-deep'
                        : 'border-light bg-paper text-ink-2 hover:bg-sand',
                    ].join(' ')}
                  >
                    {year}
                  </button>
                ))}
              </div>
            ) : (
              /* A fact, not a field. See the note above `inCardChangeover`. */
              <p id="cardYear" className="tabular text-[15px]">
                {effectiveCardYear}
              </p>
            )}
          </Field>
        ) : null}

        <Field
          label="First session"
          htmlFor="firstSession"
          hint="Choose your first session date. You can adjust individual session dates before creating your league."
        >
          <Input
            id="firstSession"
            type="date"
            value={firstSessionAt}
            onChange={(e) => setFirstSessionAt(e.target.value)}
            required
          />
        </Field>

        {scheduleFields.length > 0 ? (
          <fieldset className="border-0 p-0">
            <legend className="text-[13px] font-medium">Session dates</legend>
            <p className="mt-0.5 text-[13px] text-ink-3">
              We&rsquo;ve filled these in weekly. Adjust any date if needed.
            </p>

            <ol className="mt-2.5 flex flex-col gap-2">
              {scheduleFields.map((value, index) => (
                <li key={index} className="flex items-center gap-3">
                  <span className="w-20 shrink-0 text-[13px] text-ink-2">Session {index + 1}</span>
                  <Input
                    type="date"
                    aria-label={`Session ${index + 1} date`}
                    value={value}
                    onChange={(event) => {
                      /*
                        Session one IS the first session date — the same fact, and keeping two
                        copies of it is how they end up disagreeing. Editing it here moves the
                        field above, which shifts every session still on its weekly default.
                      */
                      if (index === 0) {
                        setFirstSessionAt(event.target.value);
                        return;
                      }
                      setMovedDates((current) => ({ ...current, [index]: event.target.value }));
                    }}
                    className="tabular w-44"
                  />
                </li>
              ))}
            </ol>
          </fieldset>
        ) : null}

        <fieldset className="border-0 p-0">
          <legend className="text-[13px] font-medium">Session time</legend>
          <div className="mt-2.5 flex flex-wrap items-start gap-3">
            <Field label="Starts" htmlFor="startTime">
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="tabular w-36"
                required
              />
            </Field>
            <Field
              label="Ends"
              htmlFor="endTime"
              {...(timesBackwards ? { error: 'Ends before it starts.' } : {})}
            >
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="tabular w-36"
                invalid={timesBackwards}
                required
              />
            </Field>
            {/* The zone is the host city's, never the computer's — so it is named (owner, 2026-10-12). */}
            {zone ? (
              <p className="mt-9 text-[14px] text-ink-2">{zoneName(zone)}</p>
            ) : null}
          </div>
        </fieldset>

        {/*
          NO MAXIMUM (owner, 2026-09-06). 28 was a guess about rooms nobody has seen; she knows how
          many chairs she has. The floor stays at four — mahjong is played four to a table, so a
          league that cannot seat one table is not a league.
        */}
        <Field
          label="Player cap"
          htmlFor="cap"
          hint="Choose the number of players you’d like to include. If your league fills, additional players will join the waitlist."
          {...(capBelowMinimum
            ? { error: `A league requires at least ${MIN_PLAYERS} players (1 full table).` }
            : {})}
        >
          <Input
            id="cap"
            type="number"
            min={MIN_PLAYERS}
            value={playerCap}
            onChange={(e) => setPlayerCap(e.target.value)}
            className="tabular w-28"
          />
        </Field>

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {/*
          Sticky, because this form grows: choosing a new market adds three more fields and pushes
          the button past the fold on a laptop. A primary action you have to go looking for is one
          a director will assume is missing.
        */}
        <div className="sticky bottom-0 -mx-6 flex items-center gap-3 border-t border-light bg-chalk/95 px-6 py-4 backdrop-blur-sm">
          <Button type="submit" disabled={!canSubmit || create.isPending || capacityFull || renewalBlocked}>
            {create.isPending ? 'Creating…' : 'Create league'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/director/leagues')}>
            Cancel
          </Button>
          {!canSubmit ? (
            <span className="text-[13px] text-ink-3">
              Needs a name, a market, the number of sessions, a first session date, and session times.
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}

